function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { useMount, useToggle } from 'react-use';
import { get, omitBy, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useForm, FormContext, useFormContext } from 'react-hook-form';
import { createStateContext } from 'react-use';
import { FloatingLabelTextField, DateRangePickerWrapper, IconTriangleUp, IconTriangleDown, IconCheckmark, IconArrowLeft, IconArrowRight, IconPin, Button, LoaderCircular, Select } from '../../index.js';
import debounce from 'debounce-promise';
export const tablesInitialState = {};
const tableObjectInitialState = {
  // internal state
  isLoading: true,
  // external state
  items: [],
  // the array of data
  itemsReturned: null,
  // the count of items returned
  rowsPerPage: 50,
  // the default page limit
  currentPage: 1,
  totalItemsFound: 0,
  firstPageOffset: 0,
  lastPageOffset: 0,
  nextPageOffset: 0,
  totalPagesFound: 0,
  selectedRows: [],
  // configurable state
  filter: null,
  sortBy: null,
  sortDirection: null
};
export const useTableContext = () => {
  const [useShareState, ShareStateProvider] = createStateContext(tableObjectInitialState);
  return [useShareState, ShareStateProvider];
};
let onLoadDatas = {}; // internal storage of data returning functions, keyed by tableId

const tablesActions = store => {
  async function setState(values) {
    let newState;
    await store.setState(prevState => {
      newState = { ...prevState,
        ...values
      };
      return newState;
    });
    return newState;
  }

  const loadDataNow = async (newState = null) => {
    const tableState = newState || store.getState();
    const recordSet = await onLoadDatas({
      filter: tableState.filter,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      limit: tableState.rowsPerPage,
      offset: (tableState.currentPage - 1) * tableState.rowsPerPage
    });

    if (!recordSet || !Array.isArray(recordSet.items) || typeof recordSet.itemsReturned !== 'number') {
      throw Error('Table.props.onLoadData() expected to return { items: [], itemsReturned: 0 }, got: ' + JSON.stringify(recordSet));
    }

    return setState({ ...tableState,
      ...recordSet,
      isLoading: false
    });
  };

  const loadData = debounce(loadDataNow, 250);
  return {
    bindTable: async ({
      onLoadData
    }) => {
      onLoadDatas = onLoadData;
      return await loadDataNow();
    },
    resetTable: () => setState(tableObjectInitialState),
    setTableFilter: async ({
      filter = {}
    }) => {
      const newState = await setState({
        isLoading: true,
        currentPage: 1,
        filter
      });
      return await loadData(newState);
    },
    setTableOrder: async ({
      sortBy = null,
      sortDirection = null
    }) => {
      const newState = await setState({
        isLoading: true,
        currentPage: 1,
        sortBy,
        sortDirection
      });
      return await loadData(newState);
    },
    setTableRowsPerPage: async ({
      rowsPerPage
    }) => {
      const newState = await setState({
        isLoading: true,
        currentPage: 1,
        rowsPerPage,
        selectedRows: []
      });
      return await loadData(newState);
    },
    setTablePage: async ({
      currentPage
    }) => {
      const newState = await setState({
        isLoading: true,
        currentPage
      });
      return await loadData(newState);
    },
    setTablePage: async ({
      currentPage
    }) => {
      const newState = await setState({
        isLoading: true,
        currentPage,
        selectedRows: []
      });
      return await loadData(newState);
    },
    setTableSelectedRows: async ({
      row,
      isChecked
    }) => {
      const tableState = store.getState();

      if (isChecked) {
        return setState({
          selectedRows: [...tableState.selectedRows, row]
        });
      } else {
        return setState({
          selectedRows: tableState.selectedRows.filter(s => s !== row)
        });
      }
    },
    setTableSelectedAllRows: async ({
      isAll
    }) => {
      const tableState = store.getState();

      if (isAll) {
        return setState({
          selectedRows: tableState.items
        });
      } else {
        return setState({
          selectedRows: []
        });
      }
    }
  };
};

const Row = ({
  data,
  keys,
  tableState,
  handleOnSelectRow
}) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, keys.map(header => {
    const value = data[header.key];
    const isSelected = tableState.selectedRows.some(s => isEqual(s.rawRow, data.rawRow));
    const cellStyle = {
      // to be able to show tooltip inside cells
      overflow: header.isCellOverflowVisible ? 'visible' : 'hidden',
      background: isSelected && 'var(--blue-100)',
      opacity: tableState.selectedRows.length === 0 ? 1 : isSelected ? 1 : 0.9
    };

    if (header.key === 'selectable') {
      return /*#__PURE__*/React.createElement("td", {
        key: header.key,
        style: cellStyle
      }, /*#__PURE__*/React.createElement("input", {
        className: "table__checkbox",
        type: "checkbox",
        name: "isSelectable",
        checked: isSelected,
        onChange: e => handleOnSelectRow(data, e.target.checked)
      }));
    }

    switch (typeof value) {
      case 'function':
        return /*#__PURE__*/React.createElement("td", {
          key: header.key,
          style: cellStyle
        }, value());

      case 'boolean':
        return /*#__PURE__*/React.createElement("td", {
          key: header.key,
          style: cellStyle
        }, value ? /*#__PURE__*/React.createElement(IconCheckmark, {
          color: "black"
        }) : '');

      case 'string':
      case 'number':
        return /*#__PURE__*/React.createElement("td", {
          key: header.key,
          style: cellStyle
        }, value);

      case 'object':
        if (value instanceof Date) {
          return /*#__PURE__*/React.createElement("td", {
            key: header.key,
            style: cellStyle
          }, locale('dateFormat.short', {
            date: value
          }));
        } else if (value !== null) {
          return /*#__PURE__*/React.createElement("td", {
            key: header.key,
            style: cellStyle
          }, value.toString());
        } else {
          return /*#__PURE__*/React.createElement("td", {
            key: header.key,
            style: cellStyle
          });
        }

      default:
        return /*#__PURE__*/React.createElement("td", {
          key: header.key
        });
    }
  }));
};

const TableFooter = ({
  tableState,
  setTablePage,
  setTableRowsPerPage,
  pagination,
  locale
}) => {
  const [showPagination, setShowPagination] = useState(false);

  const nextPage = () => {
    if (!tableState) return;

    if (tableState.currentPage < tableState.totalPagesFound) {
      setTablePage({
        currentPage: tableState.currentPage + 1
      });
    }
  };

  const previousPage = () => {
    if (!tableState) return;

    if (tableState.currentPage !== 1) {
      setTablePage({
        currentPage: tableState.currentPage - 1
      });
    }
  };

  if (!tableState.items || tableState.items.length === 0) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "table-footer"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pagination"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pagination__text"
  }, locale('shared.rowsPerPage')), /*#__PURE__*/React.createElement("div", {
    className: "pagination__actions"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pagination__select-count",
    onClick: () => setShowPagination(!showPagination)
  }, tableState.rowsPerPage, "\xA0\xA0", /*#__PURE__*/React.createElement(IconTriangleDown, {
    color: "text-secondary",
    size: 6
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames('pagination__pages', showPagination ? 'pagination__pages--active' : '')
  }, pagination.map((nb, index) => /*#__PURE__*/React.createElement("div", {
    key: index,
    className: nb === tableState.itemsPerPage ? '' : '',
    onClick: () => {
      setTableRowsPerPage({
        rowsPerPage: nb
      });
      setShowPagination(false);
    }
  }, nb)))), /*#__PURE__*/React.createElement("div", {
    onClick: () => previousPage(),
    className: "pagination__previous"
  }, /*#__PURE__*/React.createElement(IconArrowLeft, {
    color: "text-secondary"
  })), /*#__PURE__*/React.createElement("div", {
    className: "pagination-text"
  }, (tableState.currentPage - 1) * tableState.rowsPerPage + 1, " -", ' ', tableState.nextPageOffset || tableState.totalItemsFound, " ", locale('shared.of'), ' ', tableState.totalItemsFound), /*#__PURE__*/React.createElement("div", {
    onClick: () => nextPage(),
    className: "pagination__next"
  }, /*#__PURE__*/React.createElement(IconArrowRight, {
    color: "text-secondary"
  }))));
};

const TableTop = ({
  getExportCSVURL,
  isEnableFullScreen,
  isFullScreen,
  title,
  locale,
  buttons,
  toggleFullScreen,
  tableState
}) => {
  return /*#__PURE__*/React.createElement("div", {
    className: !tableState || tableState.isLoading ? 'table__actions table__actions--loading' : 'table__actions'
  }, /*#__PURE__*/React.createElement("div", {
    className: "table__title"
  }, title && /*#__PURE__*/React.createElement("h1", {
    className: "h3"
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: "_flex-row"
  }, buttons && buttons(), getExportCSVURL && /*#__PURE__*/React.createElement(Button, {
    label: locale('shared.exportCSV'),
    version: "monochrome",
    icon: "Download",
    iconSize: 11,
    className: "table__actions__exportcsv",
    onClick: async () => window.location.href = await getExportCSVURL(),
    isReverse: true
  }), isEnableFullScreen && /*#__PURE__*/React.createElement(Button, {
    onClick: toggleFullScreen,
    version: "monochrome",
    icon: isFullScreen ? 'Exit' : 'FullScreen',
    iconSize: 13,
    isReverse: true
  })));
};

const TableHeader = ({
  currentHeaders,
  disableSort,
  enablePin,
  headers,
  locale,
  setCurrentHeaders,
  setTableFilter,
  setTableOrder,
  setTableSelectedAllRows,
  tableState
}) => {
  function setOrder(header) {
    if (!tableState || tableState.isLoading) return;

    if (header.key === tableState.sortBy) {
      setTableOrder({
        sortBy: header.key,
        sortDirection: tableState.sortDirection === 'ASC' ? 'DESC' : 'ASC'
      });
    } else {
      setTableOrder({
        sortBy: header.key,
        sortDirection: 'ASC'
      });
    }
  }

  function pinHeader(header) {
    let headerToPin = headers.findIndex(_ => _.key === header.key);

    if (headerToPin !== -1) {
      if (headers[headerToPin].isPin) headers[headerToPin].isPin = false;else headers[headerToPin].isPin = true;
      setCurrentHeaders(cloneDeep(headers).sort((a, b) => {
        if (!a.isPin) {
          if (!b.isPin) return 0;
          return 1;
        } else {
          if (!b.isPin) return -1;
          return 0;
        }
      }));
    }
  }

  function applyFilter(filter) {
    if (!tableState) return;
    const stateFilter = get(tableState, 'filter') || {};
    const newFilter = { ...stateFilter,
      ...filter // update/append filter to list

    };
    const cleanFilter = omitBy(newFilter, e => e === '' || e === null || e === undefined);
    return setTableFilter({
      filter: cleanFilter
    });
  }

  function handleOnisSelectAll(isAll) {
    setTableSelectedAllRows({
      isAll
    });
  }

  const {
    triggerValidation,
    setValue,
    register
  } = useFormContext();
  return /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, currentHeaders.map((header, index) => {
    return /*#__PURE__*/React.createElement("th", {
      key: index
    }, /*#__PURE__*/React.createElement("div", {
      className: "table__header"
    }, /*#__PURE__*/React.createElement("div", {
      className: "table__header__info"
    }, !disableSort && !header.disableSort ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "table__header__sort"
    }, /*#__PURE__*/React.createElement(IconTriangleUp, {
      className: "table__header__arrow",
      color: tableState && tableState.sortBy === header.key && tableState.sortDirection === 'ASC' ? 'black' : 'grey-900',
      size: 6.5
    }), /*#__PURE__*/React.createElement(IconTriangleDown, {
      className: "table__header__arrow",
      color: tableState && tableState.sortBy === header.key && tableState.sortDirection !== 'ASC' ? 'black' : 'grey-900',
      size: 6.5
    })), /*#__PURE__*/React.createElement("div", {
      className: classNames('table__header__title', {
        'table__header__title--sorted': tableState && tableState.sortBy === header.key
      }),
      onClick: () => setOrder(header)
    }, header.label)) : /*#__PURE__*/React.createElement("div", {
      className: "table__header__title--disabled"
    }, header.label), enablePin && /*#__PURE__*/React.createElement("div", {
      onClick: () => pinHeader(header)
    }, /*#__PURE__*/React.createElement(IconPin, {
      className: header.isPin ? 'table__header--pinned' : '',
      color: "black",
      size: 12
    })))), header.isFiltered && !header.isFilteredByDate && /*#__PURE__*/React.createElement("div", {
      className: "table__header__filter"
    }, /*#__PURE__*/React.createElement(FloatingLabelTextField, {
      inputRef: register,
      type: "text",
      name: header.key,
      onChange: async e => {
        const value = e.target.value;
        await setValue(header.key, value, true);

        try {
          const isValid = await triggerValidation(header.key);

          if (isValid) {
            applyFilter({
              [header.key]: value
            });
          }
        } catch (error) {} finally {
          return value;
        }
      },
      isMini: true
    })), !header.isFiltered && header.isFilteredByDate && /*#__PURE__*/React.createElement("div", {
      className: "table__header__filter"
    }, /*#__PURE__*/React.createElement(DateRangePickerWrapper, {
      inputRef: register,
      id: header.key,
      startDatePlaceholderText: locale('shared.startDate').toString(),
      startDateId: "startDate",
      endDatePlaceholderText: locale('shared.endDate').toString(),
      endDateId: "endDate",
      enableOutsideDays: true,
      isOutsideRange: header.isOutsideRange,
      showClearDates: true,
      displayFormat: "DD/MM/YYYY",
      onChange: async value => {
        const sanitizeDate = string => {
          const dateObj = new Date(Date.parse(string));
          const newDate = new Date(dateObj.setUTCHours(0));
          return newDate.toISOString();
        };

        const startDate = value.startDate ? sanitizeDate(value.startDate) : undefined;
        const endDate = value.endDate ? sanitizeDate(value.endDate) : undefined;
        await setValue(header.key, startDate, true);
        await setValue(header.key, endDate, true);
        applyFilter({
          datetimeFrom: startDate,
          datetimeUntil: endDate
        });
      }
    })), header.key === 'selectable' && /*#__PURE__*/React.createElement("div", {
      className: "table__header__filter"
    }, /*#__PURE__*/React.createElement("input", {
      className: "table__checkbox",
      type: "checkbox",
      name: "isSelectAll",
      checked: tableState.selectedRows.length === tableState.items.length,
      onChange: e => handleOnisSelectAll(e.target.checked)
    })), header.multi && header.multi.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "table__header__filter"
    }, /*#__PURE__*/React.createElement(Select, {
      name: "isMulti",
      className: "table__header__filter--selectable",
      small: true,
      isSearchable: true,
      isMultiple: true,
      values: header.multi,
      onChange: async selectedArray => {
        if (selectedArray && selectedArray.length > 0) {
          applyFilter({
            [header.key]: selectedArray
          });
        }

        if (selectedArray && selectedArray.length === 0) {
          applyFilter({
            [header.key]: undefined
          });
        }
      }
    })), header.single && header.single.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "table__header__filter"
    }, /*#__PURE__*/React.createElement(Select, {
      name: "isSingle",
      className: "table__header__filter--selectable",
      small: true,
      isSearchable: true,
      isMultiple: false,
      sortOptions: false,
      values: header.single,
      onChange: async selected => {
        if (selected) {
          applyFilter({
            [header.key]: selected
          });
        }

        if (selected === '') {
          applyFilter({
            [header.key]: undefined
          });
        }
      }
    })));
  })));
};

const TableBody = ({
  tableState,
  currentHeaders,
  locale,
  handleOnSelectRow
}) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tbody", {
    className: "table__body"
  }, (!tableState || tableState.isLoading) && /*#__PURE__*/React.createElement("div", {
    className: "table__body--loading"
  }, /*#__PURE__*/React.createElement("span", {
    className: "table__loader"
  }, /*#__PURE__*/React.createElement(LoaderCircular, {
    size: 20,
    thickness: 3,
    color: "blue-600"
  }))), tableState && tableState.items && tableState.items.map((value, index) => /*#__PURE__*/React.createElement("tr", {
    key: index
  }, /*#__PURE__*/React.createElement(Row, {
    key: index,
    data: value,
    keys: currentHeaders,
    tableState: tableState,
    handleOnSelectRow: handleOnSelectRow
  }))), tableState && !tableState.isLoading && tableState.itemsReturned === 0 && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: currentHeaders.length
  }, locale('shared.noData')))));
};

const TableContainer = ({
  headers,
  // array of objects
  pagination = false,
  // false or array of integers
  disableSort = false,
  enablePin = false,
  getExportCSVURL,
  isEnableFullScreen = true,
  isSelectable = false,
  title,
  locale,
  buttons,
  // dom element
  EmptyState,
  tableState,
  // state
  // actions
  setTableFilter,
  setTableOrder,
  setTablePage,
  setTableRowsPerPage,
  setTableSelectedRows,
  setTableSelectedAllRows
}) => {
  const selectableHeader = isSelectable ? [{
    key: 'selectable',
    label: '',
    isPin: true,
    disableSort: true
  }] : [];
  const [currentHeaders, setCurrentHeaders] = useState([...selectableHeader, ...headers]);
  const [isFullScreen, toggleFullScreen] = useToggle(false);

  const handleOnSelectRow = (row, isChecked) => setTableSelectedRows({
    row,
    isChecked
  });

  return EmptyState && tableState && !tableState.isLoading && tableState.itemsReturned === 0 && (!tableState.filter || Object.keys(tableState.filter).length === 0) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmptyState, null)) : /*#__PURE__*/React.createElement("div", {
    className: isFullScreen ? 'table--fullscreen' : ''
  }, (getExportCSVURL || isEnableFullScreen || buttons || title) && /*#__PURE__*/React.createElement(TableTop, {
    getExportCSVURL,
    isEnableFullScreen,
    isFullScreen,
    title,
    locale,
    buttons,
    toggleFullScreen,
    tableState
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("table", {
    className: !tableState || tableState.isLoading ? 'table table--loading' : 'table'
  }, /*#__PURE__*/React.createElement(TableHeader, {
    tableState,
    locale,
    currentHeaders,
    enablePin,
    disableSort,
    headers,
    setCurrentHeaders,
    setTableFilter,
    setTableOrder,
    setTableSelectedAllRows
  }), /*#__PURE__*/React.createElement(TableBody, {
    tableState,
    currentHeaders,
    locale,
    handleOnSelectRow
  }))), pagination && tableState && !tableState.isLoading && /*#__PURE__*/React.createElement(TableFooter, {
    tableState,
    pagination,
    setTablePage,
    setTableRowsPerPage,
    locale
  }));
};

const TableView = props => {
  const onSubmit = () => {};

  const {
    bindTable,
    onLoadData
  } = props;
  useMount(() => bindTable({
    onLoadData
  }));
  const initialValues = props.headers.reduce((acc, header) => ({ ...acc,
    [header.key]: ''
  }), {});
  const form = useForm({
    validationSchema: props.validationSchema,
    mode: 'onChange',
    defaultValues: initialValues
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "table__container"
  }, /*#__PURE__*/React.createElement(FormContext, form, /*#__PURE__*/React.createElement("form", {
    onSubmit: form.handleSubmit(onSubmit)
  }, /*#__PURE__*/React.createElement(TableContainer, props))));
};

export const Table = ({
  onChangeTable,
  useTableState,
  ...props
}) => {
  const [localState, setLocalState] = useTableState ? useTableState() : useState(tableObjectInitialState);
  const store = {
    getState: () => localState,
    setState: state => setLocalState(state)
  };
  useEffect(() => {
    onChangeTable && onChangeTable(localState);
  }, [localState]);
  return /*#__PURE__*/React.createElement(TableView, _extends({}, props, {
    tableState: localState
  }, tablesActions(store)));
};
Table.propTypes = {
  locale: PropTypes.func.isRequired,
  onLoadData: PropTypes.func.isRequired,
  headers: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isPin: PropTypes.bool,
    disableSort: PropTypes.bool,
    isFiltered: PropTypes.bool,
    isOverflowVisible: PropTypes.bool,
    isFilteredByDate: PropTypes.bool,
    multi: PropTypes.array
  })).isRequired,
  pagination: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.bool]).isRequired,
  disableSort: PropTypes.bool,
  enablePin: PropTypes.bool,
  isEnableFullScreen: PropTypes.bool,
  getExportCSVURL: PropTypes.func,
  isSelectable: PropTypes.bool,
  useTableState: PropTypes.func,
  onChangeTable: PropTypes.func
};